/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactElement, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router'

import { defaultBannerContent, defaultCookieProperties, defaultStyleClasses, ID_PAGE_MAIN_ELEMENT } from '../../constants'
import { clearCart } from '../../features/cart/cartSlice'
import { useCustomGADimensions } from '../../hooks/useCustomGADimensions'
import { useLoadPayload } from '../../hooks/useLoadPayload'
import { useAppDispatch, useAppSelector } from '../../store'
import { scrollPageTo } from '../../utils'
import { env } from '../../utils/env'
import { isPrivateMode } from '../../utils/is-private-mode'
import Icon from '../atoms/images/icon'
import CookiesBanner from '../cookieBanner'
import Footer from '../footer'
import Header from '../header'
import NewsletterSubForm from '../newsletterSubsForm'
import './page.css'

type PageProps = {
  metaData: {
    charset: string
    description: string
    themeColor?: string
    title: string
  }
  className: string
}

const Page: FC<PageProps> = ({ metaData, children, className }): ReactElement => {
  const { title, charset, description } = metaData
  const mainRef = useRef<HTMLDivElement>(null)
  const scrollButtonRef = useRef<HTMLButtonElement>(null)
  const [scrollTop, setScrollTop] = useState(0)

  const dispatch = useAppDispatch()
  const { settings, cookieBannerValue } = useAppSelector((state) => state.appConfigReducer)

  const cookieProperties = {
    ...defaultCookieProperties,
    cookieValue: cookieBannerValue,
  }
  const location = useLocation()
  const executeScroll = () => {
    mainRef.current && mainRef.current.scrollTo(0, 0)
  }

  const onScroll = () => {
    const scrollTop = mainRef.current?.scrollTop as number
    setScrollTop(scrollTop)
  }
  useEffect(() => {
    // eslint-disable-next-line no-console
    isPrivateMode().then((res) => console.log(`Running incognito mode: ${res}`))
    window.addEventListener(
      'message',
      (e: any) => {
        if (e.origin !== env('REACT_APP_EVENT_ORIGIN')) {
          return
        }

        if (e.data === 'CheckoutComplete') {
          dispatch(clearCart())
        }
      },
      false
    )
  }, [dispatch])

  useEffect(() => {
    if (mainRef.current && scrollButtonRef.current)
      scrollTop > 500 ? (scrollButtonRef.current.style.display = 'block') : (scrollButtonRef.current.style.display = 'none')
  }, [scrollTop])

  useEffect(() => {
    if (mainRef.current) {
      executeScroll()
      const focusElement = document.querySelector('#focus-element') as HTMLElement
      focusElement.focus()
    }
  }, [location.search, mainRef])
  useEffect(() => {
    if (location.hash !== '') {
      scrollPageTo(location.hash.substring(1))
    }
  }, [location.hash])

  useCustomGADimensions()

  useLoadPayload()

  return (
    <>
      <Helmet>
        <html lang={settings.locale.language} />
        <meta name="theme-color" content={metaData.themeColor || '#fff'} />
        <meta charSet={charset} />
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <div className="cookiebanner">
        <CookiesBanner bannerContent={defaultBannerContent} cookieProperties={cookieProperties} styleClasses={defaultStyleClasses} />
      </div>
      <Header />
      <span id="focus-element" aria-hidden></span>
      <main ref={mainRef} onScroll={onScroll} className={`page ${className}`} id={ID_PAGE_MAIN_ELEMENT}>
        {children}
        <button ref={scrollButtonRef} className="click-to-scroll" onClick={executeScroll} aria-label="Scroll to Top">
          <Icon className="top-scroll-icon" icon="topButton.svg" size={{ width: 25, height: 25 }} alt="button-to-scrollUp" />
        </button>
        <NewsletterSubForm />
        <Footer />
      </main>
    </>
  )
}

export default Page
